
import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BugReportForm } from "./BugReportForm";
import { BugReportsList } from "./BugReportsList";
import { useBugReports, BugReportStatus, BugReportCreateInput } from "@/hooks/useBugReports";
import { useAuth } from "@/hooks/useAuth";
import { useUserRoles } from "@/hooks/useUserRoles";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";

export function BugReportsManager() {
  const { user } = useAuth();
  const { useHasRoleQuery } = useUserRoles();
  const [activeTab, setActiveTab] = useState("reports");
  const [viewMode, setViewMode] = useState<"active" | "completed" | "mine">("active");
  
  const { data: hasTesterRoleData } = useHasRoleQuery("TESTER");
  const { data: hasAdminRoleData } = useHasRoleQuery("ADMIN");
  const { data: hasDevRoleData } = useHasRoleQuery("DEVELOPER");
  
  const hasRequiredRole = hasTesterRoleData || hasAdminRoleData || hasDevRoleData;
  
  const {
    useBugReportsQuery,
    useCreateBugReportMutation,
    useUpdateBugReportMutation
  } = useBugReports();
  
  const { data: reports = [], isLoading } = useBugReportsQuery();
  const createMutation = useCreateBugReportMutation();
  const updateMutation = useUpdateBugReportMutation();
  
  const filteredReports = reports.filter(report => {
    if (viewMode === "active") return report.status !== "Complete";
    if (viewMode === "completed") return report.status === "Complete";
    if (viewMode === "mine") return report.status === "In Work" && report.userId === user?.id;
    return true;
  });
  
  const handleCreateReport = async (data: BugReportCreateInput) => {
    try {
      await createMutation.mutateAsync(data);
      setActiveTab("reports");
    } catch (error) {
      console.error("Failed to create bug report:", error);
    }
  };
  
  const handleUpdateStatus = async (id: string, status: BugReportStatus) => {
    try {
      await updateMutation.mutateAsync({ id, status });
    } catch (error) {
      console.error("Failed to update bug report status:", error);
    }
  };
  
  if (!user) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Authentication Required</AlertTitle>
        <AlertDescription>
          You must be logged in to access the bug reporting system.
        </AlertDescription>
      </Alert>
    );
  }
  
  if (!hasRequiredRole) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Access Denied</AlertTitle>
        <AlertDescription>
          You must have TESTER, ADMIN, or DEVELOPER role to access the bug reporting system.
        </AlertDescription>
      </Alert>
    );
  }
  
  return (
    <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="reports">View Reports</TabsTrigger>
          <TabsTrigger value="submit">Submit New Report</TabsTrigger>
        </TabsList>
        
        <TabsContent value="reports" className="mt-6">
          <BugReportsList 
            reports={filteredReports}
            isLoading={isLoading}
            onUpdateStatus={handleUpdateStatus}
            isUpdating={updateMutation.isPending}
            viewMode={viewMode}
            onChangeViewMode={setViewMode}
          />
        </TabsContent>
        
        <TabsContent value="submit" className="mt-6">
          <BugReportForm 
            onSubmit={handleCreateReport}
            isSubmitting={createMutation.isPending}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
